import { useEffect, useMemo, useState } from "react";

import { Button, Callout, Checkbox, Icon, Intent } from "@blueprintjs/core";
import { addYears } from "date-fns";

import { Box } from "@/components/layout/flexbox";
import { ParticipantFormInput } from "@/components/pages_logged_in/program/components/set-components/configure-inputs/ParticipantFormInput";
import { DateTimeInputV2 } from "@/components/pieces/DateTimeInputV2";
import { StyledDialog } from "@/components/pieces/StyledDialog";
import { SUBTEXT_COLOR } from "@/css/constants";
import {
  ProgramComponentsQueryDocument,
  ProgramQueryDocument,
  ProgramUserRole,
  useGrantExtensionComponentMutation,
  useSimpleProgramUsersQueryLazyQuery,
} from "@/graphql";
import { LeadComponent, MainComponent } from "@/types/programs";
import { SelectOption } from "@/types/select";
import { ensureNonNullArray, formatErrors } from "@/utils/array";
import { getBrowserTimezone } from "@/utils/datetime";

interface GrantExtensionDialogsProps {
  open: boolean;
  onClose: () => void;
  component: LeadComponent;
}

interface GrantExtensionInfoProps {
  component: LeadComponent | MainComponent;
  grantedCloseDate?: string;
  participantCount?: number;
}

function GrantExtensionInfo({ component }: GrantExtensionInfoProps) {
  const children = component.childComponents || [];
  const prerequisiteComponent = component?.prerequisiteComponent;

  const hasRelatedComponents = children.length > 0 || prerequisiteComponent;
  const totalAffected = children.length + (prerequisiteComponent ? 1 : 0);

  if (!hasRelatedComponents) {
    return null; // No info to show if no related components
  }

  return (
    <Box className="my-4">
      {/* Main Info Box */}
      <Callout intent={Intent.PRIMARY} className="mb-3">
        <Box className="text-sm leading-relaxed">
          <Box>
            This extension will also update {totalAffected - 1} related
            component{totalAffected - 1 > 1 ? "s" : ""}:
          </Box>

          {/* Children Components */}
          {children.length > 0 && (
            <Box className="mt-2">
              <Box className="ml-4.5 text-sm text-gray-600">
                {children.map((child) => (
                  <Box key={child.id} className="mb-0.5">
                    • {child.name}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Dependent Components */}
          {prerequisiteComponent && (
            <Box className="mt-2">
              <Box className="ml-4.5 text-sm text-gray-600">
                <Box key={prerequisiteComponent.id} className="mb-0.5">
                  • {prerequisiteComponent.name}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Callout>
    </Box>
  );
}

export function GrantExtensionDialogs({
  open,
  onClose,
  component,
}: GrantExtensionDialogsProps) {
  const [errorMessage, setErrorMessage] = useState<string>();
  const [grantedCloseDate, setGrantedCloseDate] = useState<string | null>();
  const [timezone, setTimezone] = useState(getBrowserTimezone());
  const [sendEmailToParticipants, setSendEmailToParticipants] = useState(true);
  const minDate = component?.grantedCloseDate
    ? new Date(component?.grantedCloseDate)
    : new Date();
  const maxDate = addYears(new Date(), 1);
  const [participants, setParticipants] = useState<SelectOption[]>([]);
  const programId = component?.programId;
  const grantedCloseDateUserIds =
    component.grantedCloseDateUserIds?.map((id) => id.toString()) || [];

  const [
    fetchProgramUser,
    { loading: loadingProgramUser, data: programUserData },
  ] = useSimpleProgramUsersQueryLazyQuery();

  const participantOptions = useMemo(
    () =>
      (ensureNonNullArray(programUserData?.simpleProgramUsers).map((item) => ({
        label: item.name ? `${item.name} (${item.email})` : item.email,
        value: item.userId,
      })) as SelectOption[]) || [],
    [programUserData],
  );

  useEffect(() => {
    if (programId) {
      fetchProgramUser({
        variables: {
          programId: programId,
          roles: [ProgramUserRole.Participant],
          componentId: component.id,
        },
      });
    }
  }, [programId]);

  useEffect(() => {
    if (component && programUserData) {
      setGrantedCloseDate(component.grantedCloseDate);
      if (
        component.grantedCloseDateUserIds?.length &&
        programUserData?.simpleProgramUsers?.length
      ) {
        setParticipants(
          participantOptions.filter((option) =>
            grantedCloseDateUserIds.includes(option.value),
          ),
        );
      }
    }
  }, [component, programUserData]);

  const [grantExtensionComponent, { loading }] =
    useGrantExtensionComponentMutation({
      onCompleted: (data) => {
        const { ok, errors } = data?.grantExtensionComponent || {};
        if (ok) {
          onClose();
        } else {
          const errorMessage = formatErrors(errors);
          setErrorMessage(errorMessage);
        }
      },
      onError: (err) => {
        setErrorMessage(err.message);
      },
      refetchQueries: [
        {
          query: ProgramQueryDocument,
          variables: {
            programId,
          },
        },
        {
          query: ProgramComponentsQueryDocument,
          variables: {
            programId,
          },
        },
      ],
    });

  const disableSubmit = useMemo(() => {
    const isValidDate =
      grantedCloseDate && new Date(grantedCloseDate) > new Date();
    return !grantedCloseDate || loading || !component || !isValidDate;
  }, [grantedCloseDate, loading, component]);

  return (
    <StyledDialog
      isOpen={open}
      lazy={true}
      title={<Box>Grant Extension</Box>}
      width="480px"
      onClose={() => {
        onClose();
      }}
      onOpening={() => {
        setErrorMessage(undefined);
      }}
    >
      <Box p={24}>
        {errorMessage && (
          <Box mb={3}>
            <Callout icon={null} intent={Intent.DANGER}>
              {errorMessage}
            </Callout>
          </Box>
        )}

        <GrantExtensionInfo component={component} />

        <Box fontWeight={600} fontSize={16} mb={2}>
          Updated Close Date
        </Box>

        <Box css={{ position: "relative" }} mb={3}>
          <Icon
            icon="calendar"
            style={{
              position: "absolute",
              top: 12,
              left: 10,
              zIndex: 99,
              color: SUBTEXT_COLOR,
            }}
          />
          <DateTimeInputV2
            onChange={setGrantedCloseDate}
            value={grantedCloseDate}
            timezone={timezone}
            onChangeTimezone={setTimezone}
            maxDateTime={maxDate}
            minDateTime={minDate}
          />
        </Box>

        <ParticipantFormInput
          participants={participants}
          isSelectedAllParticipants={false}
          participantOptions={participantOptions}
          programComponent={component as MainComponent}
          loadingProgramUser={loadingProgramUser}
          programUserData={programUserData}
          setParticipants={setParticipants}
        />

        <Box mt={3} mb={3}>
          <Checkbox
            checked={sendEmailToParticipants}
            onChange={(e) =>
              setSendEmailToParticipants(e.currentTarget.checked)
            }
            label="Send email notifications to recipients"
            className="text-base mb-1"
          />
        </Box>

        <Box mt={3}>
          <Button
            intent="primary"
            large={true}
            disabled={disableSubmit}
            onClick={() => {
              const addProgramUserIds = participants.map((p) => p.value);
              const removeProgramUserIds = grantedCloseDateUserIds.filter(
                (id) => !addProgramUserIds.includes(id.toString()),
              );
              grantExtensionComponent({
                variables: {
                  componentId: component.id,
                  grantedCloseDate: grantedCloseDate ?? "",
                  addProgramUserIds: addProgramUserIds,
                  removeProgramUserIds: removeProgramUserIds,
                  shouldNotify: sendEmailToParticipants,
                },
              });
            }}
            loading={loading}
          >
            Grant
          </Button>
        </Box>
      </Box>
    </StyledDialog>
  );
}
